import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchCompetencesGeneriquesStepRequest, saveAllCompetencesGeneriquesManagerEvalRequest } from "../../../../redux/actions/performanceActions";
import { asArray, Field, StepState } from "../../Collaborateur/performanceUi";

export default function CompetencesGeneriquesManagerStep({ progressId }) {
    const dispatch = useDispatch();
    const slice = useSelector((state) => state.performance.competencesGeneriques);
    const data = slice.stepByProgressId[String(progressId)];
    const loading = slice.loadingByProgressId[String(progressId)];
    const error = slice.errorByProgressId[String(progressId)];
    const competences = asArray(data?.competences);
    const evaluations = asArray(data?.evaluations);
    const [drafts, setDrafts] = useState({});
    const [selectedDetailCompetence, setSelectedDetailCompetence] = useState(null);

    useEffect(() => {
        dispatch(fetchCompetencesGeneriquesStepRequest(progressId));
    }, [dispatch, progressId]);

    const autoEval = useMemo(() => evaluations.reduce((acc, evaluation) => {
        if (evaluation.typeEvaluation === "AUTO_EVALUATION") acc[evaluation.competenceGeneriqueId] = evaluation;
        return acc;
    }, {}), [evaluations]);

    const managerEval = useMemo(() => evaluations.reduce((acc, evaluation) => {
        if (evaluation.typeEvaluation === "MANAGER_EVALUATION") acc[evaluation.competenceGeneriqueId] = evaluation;
        return acc;
    }, {}), [evaluations]);

    const updateDraft = (id, patch) => setDrafts((current) => ({ ...current, [id]: { ...(current[id] || {}), ...patch } }));

    const saveAll = () => {
        const items = competences.map((competence) => {
            const collaboratorEvaluation = autoEval[competence.id] || {};
            const previous = managerEval[competence.id] || {};
            const draft = drafts[competence.id] || {};
            return {
                evaluationProgressId: Number(progressId),
                competenceGeneriqueId: competence.id,
                niveauEvalue: draft.niveauEvalue ?? previous.niveauEvalue ?? collaboratorEvaluation.niveauEvalue,
                commentaire: draft.commentaire ?? previous.commentaire ?? "Validation manager",
            };
        });
        if (items.some((item) => !item.niveauEvalue)) {
            window.alert("Veuillez saisir un niveau manager pour chaque competence.");
            return;
        }
        dispatch(saveAllCompetencesGeneriquesManagerEvalRequest(Number(progressId), items));
    };

    const completedCount = competences.filter((c) => managerEval[c.id]?.id).length;

    return (
        <StepState loading={loading} error={error} empty={!data || competences.length === 0}>
            {/* Bandeau statut */}
            <div className={`perf-state perf-state--inline ${data?.managerEvaluationCompleted ? "perf-state--success" : "perf-state--warning"}`}>
                {data?.managerEvaluationCompleted
                    ? "✓ Évaluations validées par le manager"
                    : data?.autoEvaluationCompleted
                        ? "En attente de la validation manager"
                        : "Aucune auto-évaluation disponible pour le moment"}
            </div>

            {/* Compteur de progression */}
            <div className="cg-progress-bar">
                <div className="cg-progress-bar__header">
                    <span className="cg-progress-bar__label">Progression</span>
                    <span className="cg-progress-bar__count">{completedCount} / {competences.length} validées</span>
                </div>
                <div className="cg-progress-track">
                    <div
                        className="cg-progress-fill"
                        style={{ width: competences.length ? `${Math.round((completedCount / competences.length) * 100)}%` : "0%" }}
                    />
                </div>
            </div>

            {/* Liste des compétences */}
            <div className="perf-cg-grid">
                {competences.map((competence) => {
                    const previous = managerEval[competence.id] || {};
                    const collaboratorEvaluation = autoEval[competence.id] || {};
                    const draft = drafts[competence.id] || {};
                    const value = draft.niveauEvalue ?? previous.niveauEvalue ?? collaboratorEvaluation.niveauEvalue ?? "";
                    const comment = draft.commentaire ?? previous.commentaire ?? "";
                    const isValidated = !!previous.id;
                    const maxLevel = competence.level || previous.niveauMax || collaboratorEvaluation.niveauMax || 4;

                    const getStatusText = () => {
                        return isValidated ? "✓ Validée" : "En attente";
                    };

                    return (
                        <div className="perf-cg-card" key={competence.id}>
                            <div>
                                <h3 className="perf-cg-card-title">{competence.nom}</h3>
                                
                                {/* Données collaborateur */}
                                <div className="perf-cg-facts">
                                    <div className="perf-cg-fact-item">
                                        <span className="perf-cg-fact-label">Note collaborateur</span>
                                        <span className="perf-cg-fact-value">
                                            {collaboratorEvaluation.niveauEvalue ?? "—"} {collaboratorEvaluation.niveauEvalue ? `/ ${maxLevel}` : ""}
                                        </span>
                                    </div>
                                    <div className="perf-cg-fact-item" style={{ flexDirection: "column", gap: "2px", marginTop: "4px" }}>
                                        <span className="perf-cg-fact-label">Commentaire collab.</span>
                                        <span style={{ color: "var(--perf-text)", fontWeight: "normal", fontSize: "11px", wordBreak: "break-word" }}>
                                            {collaboratorEvaluation.commentaire || "—"}
                                        </span>
                                    </div>
                                </div>

                                {/* Saisie manager */}
                                <div className="perf-cg-score-row">
                                    <input 
                                        type="number" 
                                        min="1" 
                                        max={maxLevel} 
                                        className="perf-cg-score-input"
                                        value={value}
                                        onChange={(event) => updateDraft(competence.id, { niveauEvalue: event.target.value })}
                                        placeholder={`1 – ${maxLevel}`}
                                    />
                                    <span className="perf-cg-score-max">/ {maxLevel}</span>
                                </div>

                                <div className="perf-cg-comment-block">
                                    <span className="perf-cg-comment-label">Commentaire manager</span>
                                    <textarea 
                                        className="perf-cg-comment-textarea"
                                        value={comment}
                                        placeholder="Votre commentaire..."
                                        onChange={(event) => updateDraft(competence.id, { commentaire: event.target.value })}
                                    />
                                </div>
                            </div>

                            <div className="perf-cg-footer">
                                <span className="perf-cg-status">{getStatusText()}</span>
                                <button 
                                    type="button" 
                                    className="perf-cg-details-btn"
                                    onClick={() => setSelectedDetailCompetence(competence)}
                                >
                                    Détails
                                </button>
                            </div>
                        </div>
                    );
                })}
            </div>

            {selectedDetailCompetence && (
                <div className="perf-modal-overlay" onClick={() => setSelectedDetailCompetence(null)}>
                    <div className="perf-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="perf-modal__header">
                            <h2>{selectedDetailCompetence.nom}</h2>
                            <button className="perf-modal__close" onClick={() => setSelectedDetailCompetence(null)}>&times;</button>
                        </div>
                        <div className="perf-modal__body">
                            <p style={{ fontSize: "13px", color: "var(--perf-muted)", lineHeight: "1.6", margin: 0 }}>
                                {selectedDetailCompetence.description || "Aucune description disponible pour cette compétence."}
                            </p>
                            {selectedDetailCompetence.categorie && (
                                <div style={{ marginTop: "12px" }}>
                                    <span className="perf-badge perf-badge--blue">{selectedDetailCompetence.categorie}</span>
                                </div>
                            )}
                        </div>
                        <div className="perf-modal__footer">
                            <button className="perf-btn" onClick={() => setSelectedDetailCompetence(null)}>Fermer</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Bouton de validation centré en bas */}
            <div className="perf-actions perf-actions--center">
                <button
                    className="perf-btn perf-btn--primary"
                    type="button"
                    disabled={competences.length === 0}
                    onClick={saveAll}
                >
                    Enregistrer et valider toutes les compétences
                </button>
            </div>
        </StepState>
    );
}
